import type { ComponentChildren, ComponentType, ErrorInfo } from "preact";
import { Component, createElement } from "preact";

import { ErrorBoundaryContext } from "./context.js";

export type FallbackProps = {
	error: unknown;
	resetErrorBoundary: (...args: unknown[]) => void;
};

export type OnErrorCallback = (error: unknown, info: ErrorInfo) => void;

type ErrorBoundarySharedProps = {
	/**
	 * Optional callback to enable e.g. logging error information to a server.
	 *
	 * @param error Value that was thrown; typically an instance of `Error`
	 * @param info React "component stack" identifying where the error was thrown
	 */
	onError?: (error: unknown, info: ErrorInfo) => void;

	/**
	 * Optional callback to be notified when an error boundary is reset so React can retry the failed render.
	 */
	onReset?: (
		details:
			| { reason: "imperative-api"; args: unknown[] }
			| {
					reason: "keys";
					prev: unknown[] | undefined;
					next: unknown[] | undefined;
			  },
	) => void;

	/**
	 * When changed, these keys will reset a triggered error boundary.
	 * This can be useful when an error condition may be tied to some specific state (that can be uniquely identified by key).
	 * See the documentation for examples of how to use this prop.
	 */
	resetKeys?: unknown[];

	children?: ComponentChildren;
};

export type ErrorBoundaryPropsWithComponent = ErrorBoundarySharedProps & {
	fallback?: never;
	/**
	 * React component responsible for returning a fallback UI based on a thrown value.
	 *
	 * ```tsx
	 * <ErrorBoundary FallbackComponent={Fallback} />
	 * ```
	 */
	FallbackComponent: ComponentType<FallbackProps>;
	fallbackRender?: never;
};

export type ErrorBoundaryPropsWithRender = ErrorBoundarySharedProps & {
	fallback?: never;
	FallbackComponent?: never;
	/**
	 * [Render prop](https://react.dev/reference/react/Children#calling-a-render-prop-to-customize-rendering) function responsible for returning a fallback UI based on a thrown value.
	 *
	 * ```tsx
	 * <ErrorBoundary fallbackRender={({ error, resetErrorBoundary }) => <div>...</div>} />
	 * ```
	 */
	fallbackRender: (props: FallbackProps) => ComponentChildren;
};

export type ErrorBoundaryPropsWithFallback = ErrorBoundarySharedProps & {
	/**
	 * Static content to render in place of an error if one is thrown.
	 *
	 * ```tsx
	 * <ErrorBoundary fallback={<div className="text-red">Something went wrong</div>} />
	 * ```
	 */
	fallback: ComponentChildren;
	FallbackComponent?: never;
	fallbackRender?: never;
};

export type ErrorBoundaryProps =
	| ErrorBoundaryPropsWithFallback
	| ErrorBoundaryPropsWithComponent
	| ErrorBoundaryPropsWithRender;

const isDevelopment = import.meta.env.DEV;

type ErrorBoundaryState =
	| {
			didCatch: true;
			error: unknown;
	  }
	| {
			didCatch: false;
			error: null;
	  };

const initialState: ErrorBoundaryState = {
	didCatch: false,
	error: null,
};

/**
 * A reusable React [error boundary](https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary) component.
 * Wrap this component around other React components to "catch" errors and render a fallback UI.
 *
 * Catches errors thrown while rendering the tree below it.
 *
 * Does not catch errors thrown during:
 * - Server side rendering
 * - Event handlers
 * - Errors thrown in the error boundary itself
 * - Async code that runs after rendering, like `setTimeout` callbacks or unresolved promises
 *
 * Event handler and async errors:
 *
 * - Use `useErrorBoundary` to pass caught errors to the nearest boundary
 * - In React 19, errors thrown from a function passed to the `startTransition` function returned by `useTransition`
 *   are caught by the nearest boundary
 *
 * ℹ️ The component provides several ways to render a fallback: `fallback`, `fallbackRender`, and `FallbackComponent`.
 * Refer to the documentation to determine which is best for your application.
 *
 * ℹ️ This is a **client component**. You can only pass props to it that are serializable or use it in files that have a `"use client";` directive.
 */
export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);

		this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
		this.state = initialState;
	}

	static override getDerivedStateFromError(error: Error) {
		return { didCatch: true, error };
	}

	resetErrorBoundary(...args: unknown[]) {
		const { error } = this.state;

		if (error !== null) {
			this.props.onReset?.({
				args,
				reason: "imperative-api",
			});

			this.setState(initialState);
		}
	}

	override componentDidCatch(error: unknown, info: ErrorInfo) {
		this.props.onError?.(error, info);
	}

	override componentDidUpdate(
		prevProps: ErrorBoundaryProps,
		prevState: ErrorBoundaryState,
	) {
		const { didCatch } = this.state;
		const { resetKeys } = this.props;

		// There's an edge case where if the thing that triggered the error happens to *also* be in the resetKeys array,
		// we'd end up resetting the error boundary immediately.
		// This would likely trigger a second error to be thrown.
		// So we make sure that we don't check the resetKeys on the first call of cDU after the error is set.

		if (
			didCatch &&
			prevState.error !== null &&
			hasArrayChanged(prevProps.resetKeys, resetKeys)
		) {
			this.props.onReset?.({
				next: resetKeys,
				prev: prevProps.resetKeys,
				reason: "keys",
			});

			this.setState(initialState);
		}
	}

	render() {
		const { children, fallbackRender, FallbackComponent, fallback } =
			this.props;
		const { didCatch, error } = this.state;

		let childToRender = children;

		if (didCatch) {
			const props: FallbackProps = {
				error,
				resetErrorBoundary: this.resetErrorBoundary,
			};

			if (typeof fallbackRender === "function") {
				childToRender = fallbackRender(props);
			} else if (FallbackComponent) {
				childToRender = createElement(FallbackComponent, props);
			} else if (fallback !== undefined) {
				childToRender = fallback;
			} else {
				if (isDevelopment) {
					console.error(
						"react-error-boundary requires either a fallback, fallbackRender, or FallbackComponent prop",
					);
				}

				throw error;
			}
		}

		return createElement(
			ErrorBoundaryContext.Provider,
			{
				value: {
					didCatch,
					error,
					resetErrorBoundary: this.resetErrorBoundary,
				},
			},
			childToRender,
		);
	}
}

function hasArrayChanged(a: unknown[] = [], b: unknown[] = []) {
	return (
		a.length !== b.length ||
		a.some((item, index) => !Object.is(item, b[index]))
	);
}
